import { Fragment, useEffect, useRef, useState } from "react";
import {
  Box,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from "@mui/material";
import { topFiveStyles as s } from "../styles/styles";

// Compact "top 5" ranked list. On narrow screens the columns overflow into a
// horizontal scroll with the first (name) column frozen — the same pattern the
// canonical CustomTable uses. columns: [{ label, align?, render(row, index) }]
// renderRowFooter(row, index), optional: full-width content under a row (e.g. an
// inline prompt); return null for rows that have none.
const TopFiveTable = ({ icon, title, tooltip, columns, rows, emptyText, renderRowFooter }) => {
  const scrollRef = useRef(null);
  const [hasScroll, setHasScroll] = useState(false);

  useEffect(() => {
    const check = () => {
      const el = scrollRef.current;
      if (el) setHasScroll(el.scrollWidth > el.clientWidth + 1);
    };
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, [columns, rows]);

  const stickyFirst = (isFirst) =>
    isFirst
      ? {
          position: "sticky",
          left: 0,
          backgroundColor: "#fff",
          zIndex: 2,
          boxShadow: hasScroll ? "4px 0 8px -4px rgba(0, 0, 0, 0.12)" : "none",
        }
      : {};

  return (
    <Box sx={s.card}>
      <Typography sx={s.title}>
        {icon}
        {title}
        {tooltip}
      </Typography>

      <Box ref={scrollRef} className="custom-scrollbar" sx={{ overflowX: "auto" }}>
        <Table size="small" sx={{ width: "100%" }}>
          <TableHead>
            <TableRow>
              {columns.map((col, index) => (
                <TableCell
                  key={`h-${index}`}
                  align={col.align || "left"}
                  sx={{ ...s.headerCell, ...stickyFirst(index === 0) }}
                >
                  {col.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {rows.length === 0 && emptyText && (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  align="center"
                  sx={{ ...s.bodyCell, borderBottom: "none", opacity: 0.6 }}
                >
                  {emptyText}
                </TableCell>
              </TableRow>
            )}
            {rows.map((row, rowIndex) => {
              const isLast = rowIndex === rows.length - 1;
              const footer = renderRowFooter ? renderRowFooter(row, rowIndex) : null;
              const divider = isLast ? "none" : s.headerCell.borderBottom;
              return (
                <Fragment key={`r-${rowIndex}`}>
                  <TableRow>
                    {columns.map((col, colIndex) => (
                      <TableCell
                        key={`c-${rowIndex}-${colIndex}`}
                        align={col.align || "left"}
                        sx={{
                          ...s.bodyCell,
                          ...stickyFirst(colIndex === 0),
                          // With a footer the divider moves below it.
                          borderBottom: footer ? "none" : divider,
                        }}
                      >
                        {col.render(row, rowIndex)}
                      </TableCell>
                    ))}
                  </TableRow>
                  {footer && (
                    <TableRow>
                      <TableCell
                        colSpan={columns.length}
                        sx={{ ...s.bodyCell, paddingTop: 0, borderBottom: divider }}
                      >
                        {/* Sticky so it stays in view while the row scrolls sideways. */}
                        <Box sx={{ position: "sticky", left: 0, display: "inline-block", maxWidth: "100%" }}>
                          {footer}
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                </Fragment>
              );
            })}
          </TableBody>
        </Table>
      </Box>
    </Box>
  );
};

export default TopFiveTable;
